// Packages
import React from "react";
import PropTypes from "prop-types";
import { Checkbox } from "@mui/material";
import styled from "styled-components";
import tw from "twin.macro";

import useControllers from "../../../controllers";
import { StyledHelperText } from "../SelectField/SelectField.styles";

const StyledTable = styled.table`
  ${tw`w-full border-collapse`}

  thead {
    background-color: #cecac1;

    th {
      ${tw`text-left text-sm font-medium text-neutral-700 py-3 px-4 border-b border-neutral-200`}
    }
  }

  tbody {
    tr {
      ${tw`border-b border-neutral-100 hover:bg-neutral-50`}

      td {
        ${tw`py-3 px-4 text-sm text-neutral-700`}
      }

      &.child-permission td:first-child {
        ${tw`pl-12`}
      }
    }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: #FFB62A;
  }
  &.Mui-checked {
    color: #FFB62A;
  }
`;

const SwitchLabel = styled.span`
  ${tw`text-xs font-medium`}
  ${props => props.isActive
    ? (props.isAllow ? tw`text-success-600` : tw`text-danger-600`)
    : tw`text-neutral-400`
  }
  transition: color 0.2s ease;
`;

const StyledPermissionSwitch = styled.label`
  ${tw`relative inline-block cursor-pointer`}
  width: 56px;
  height: 28px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  /* Green when allow (checked) - matches text-success-600 */
  input:checked + .switch-slider {
    background-color: #018145;
    border-color: #018145;
  }

  input:checked + .switch-slider:before {
    background-color: white;
    border-color: #018145;
    transform: translateX(27px);
  }

  /* Red when deny (not checked) */
  input:not(:checked) + .switch-slider {
    background-color: #EF4444;
    border-color: #EF4444;
  }

  input:not(:checked) + .switch-slider:before {
    background-color: white;
    border-color: #DC2626;
  }

  .switch-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #EF4444;
    border: 1px solid #DAE0E8;
    transition: 0.3s;
    border-radius: 34px;

    &:before {
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
      position: absolute;
      content: "";
      height: 24px;
      width: 24px;
      left: 1px;
      bottom: 1px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
      border: 1px solid #dae0e8;
    }
  }
`;

const SwitchContainer = styled.div`
  ${tw`flex items-center justify-center`}
  gap: 0.5rem;
`;

const PermissionOverridesGroup = (props) => {
  const { groupedPermissions, control, name, className, rolePermissions = [] } = props;

  const { useComponentHooks } = useControllers();
  const { usePermissionOverrides } = useComponentHooks();

  const {
    value,
    ref,
    error,
    getPermissionState,
    handlePermissionChange,
  } = usePermissionOverrides(control, name);

  // Flatten permissions into a single list - show ALL permissions (standalone + children)
  const flattenedPermissions = [];
  groupedPermissions.forEach(group => {
    if (group.children.length === 0) {
      // Standalone permission (dashboard, payments, notifications, etc.)
      flattenedPermissions.push({
        ...group.parent,
        isChild: false,
      });
    } else {
      // Parent with children - show both parent and children for clarity
      // Add parent as a header (non-interactive)
      flattenedPermissions.push({
        ...group.parent,
        isParent: true,
        isChild: false,
      });
      // Add children
      group.children.forEach(child => {
        flattenedPermissions.push({
          ...child,
          isChild: true,
        });
      });
    }
  });

  const isFromRole = (permissionId) => {
    return rolePermissions.some(p => p.id === permissionId);
  };

  const handleOverrideToggle = (permissionId, currentState) => {
    if (currentState === "DEFAULT") {
      // Enable override with ALLOW
      handlePermissionChange(permissionId, "ALLOW");
    } else {
      // Disable override (back to DEFAULT)
      handlePermissionChange(permissionId, "DEFAULT");
    }
  };

  const handleSwitchToggle = (permissionId, currentType) => {
    const newType = currentType === "ALLOW" ? "DENY" : "ALLOW";
    handlePermissionChange(permissionId, newType);
  };

  return (
    <div className={className}>
      <StyledTable>
        <thead>
          <tr>
            <th className="font-semibold">Permiso</th>
            <th style={{ width: '120px', textAlign: 'center' }}>Sobrescribir</th>
            <th style={{ width: '180px', textAlign: 'center' }}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {flattenedPermissions.map((permission, index) => {
            // Parent headers are non-interactive
            if (permission.isParent) {
              return (
                <tr key={`parent-${index}`} className="bg-[#cecac1]">
                  <td colSpan="3" className="text-neutral-700 py-2">
                    {permission.label}
                  </td>
                </tr>
              );
            }

            const state = getPermissionState(permission.value);
            const hasOverride = state !== "DEFAULT";
            const fromRole = isFromRole(permission.value);
            const rolePermission = rolePermissions.find(p => p.id === permission.value);
            const roleType = rolePermission?.type;
            // null type means inherited from role, treat as ALLOW
            const effectiveRoleType = roleType === null ? "ALLOW" : roleType;
            const isAllow = state === "ALLOW";

            return (
              <tr key={`perm-${index}`} className={permission.isChild ? 'child-permission' : ''}>
                <td>
                  {permission.label}
                  {fromRole && !hasOverride && effectiveRoleType && (
                    <span className="ml-2 text-xs text-neutral-400">
                      ({effectiveRoleType === "ALLOW" ? "heredado: permitir" : "heredado: denegar"})
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <StyledCheckbox
                    checked={hasOverride}
                    onChange={() => handleOverrideToggle(permission.value, state)}
                    size="small"
                    inputRef={ref}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  {hasOverride ? (
                    <SwitchContainer>
                      <SwitchLabel isActive={!isAllow} isAllow={false}>
                        Denegar
                      </SwitchLabel>
                      <StyledPermissionSwitch htmlFor={`permission-${permission.value}`}>
                        <input
                          type="checkbox"
                          id={`permission-${permission.value}`}
                          checked={isAllow}
                          onChange={() => handleSwitchToggle(permission.value, state)}
                        />
                        <span className="switch-slider" />
                      </StyledPermissionSwitch>
                      <SwitchLabel isActive={isAllow} isAllow={true}>
                        Permitir
                      </SwitchLabel>
                    </SwitchContainer>
                  ) : (
                    <span className="text-neutral-400 text-xs">
                      {fromRole && effectiveRoleType ? (effectiveRoleType === "ALLOW" ? "Permitir" : "Denegar") : "Sin acceso"}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
      {error && <StyledHelperText>{error?.message}</StyledHelperText>}
    </div>
  );
};

PermissionOverridesGroup.propTypes = {
  groupedPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      parent: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
      children: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        })
      ),
    })
  ).isRequired,
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  rolePermissions: PropTypes.array,
};

export default PermissionOverridesGroup;
